import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Globe, Sparkles, Trash2, Plus, Users, User as UserIcon, Check } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useI18n, formatTime12, formatPhone } from "@/lib/i18n";
// import ChatBubble from "@/components/ChatBubble"; // hidden per owner request

const SIDEBAR_IMAGE = "https://images.pexels.com/photos/7664093/pexels-photo-7664093.jpeg";

function StepHeader({ step, total }) {
  return (
    <div className="flex items-center gap-2 mb-10" data-testid="booking-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} className={`h-[2px] flex-1 ${i <= step - 1 ? "bg-zen-primary" : "bg-zen-border"}`} />
      ))}
    </div>
  );
}

export default function BookingPage() {
  const { lang, setLang, t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("individual");
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [picked, setPicked] = useState([]);
  const [staffId, setStaffId] = useState("any");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [members, setMembers] = useState([{ name: "", services: [], staffId: "any" }]);
  const [submitting, setSubmitting] = useState(false);
  // Reason text returned by backend when /availability returns 0 slots — used
  // to show a friendly explanation in the time-picker step instead of a blank.
  const [slotsReason, setSlotsReason] = useState("");
  // Combo flow result: backend-built chain of segments, used when the user
  // picks a slot in combo mode (each segment has its assigned staff_id).
  const [comboChain, setComboChain] = useState(null);
  // Map of service_id -> last stylist_id this customer used (from /public/customer/{phone}).
  // Used on the staff-selection step to suggest the prior stylist.
  const [lastByService, setLastByService] = useState({});
  const [customerKnown, setCustomerKnown] = useState(false);

  // Fetch services + staff on mount, and re-fetch every 60 seconds in case
  // the salon adds/edits staff or services while the customer has the page
  // open. Also re-fetch when the customer returns to step 0 (mode picker).
  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [s1, s2] = await Promise.all([api.get("/public/services"), api.get("/public/staff")]);
        if (cancelled) return;
        setServices(s1.data);
        setStaff(s2.data);
      } catch (e) { /* noop */ }
    };
    fetchAll();
    const id = setInterval(fetchAll, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Upcoming appointments under this phone (Feature #10 — prevent dup bookings).
  // Auto-populated when phone has 10 digits and the customer is "found".
  // Auto-empties when phone changes / customer not found / appointment passed.
  const [upcomingAppts, setUpcomingAppts] = useState([]);

  useEffect(() => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) {
      api.get(`/public/customer/${digits}`).then(({ data }) => {
        if (data.found) {
          if (!name) setName(data.name);
          setCustomerKnown(true);
          setLastByService(data.last_stylist_by_service || {});
          setUpcomingAppts(data.upcoming_appointments || []);
        } else {
          setCustomerKnown(false);
          setLastByService({});
          setUpcomingAppts([]);
        }
      }).catch(() => { setCustomerKnown(false); setLastByService({}); setUpcomingAppts([]); });
    } else {
      setCustomerKnown(false);
      setLastByService({});
      setUpcomingAppts([]);
    }
  }, [phone]); // eslint-disable-line

// Format a Date object as YYYY-MM-DD using LOCAL date components.
// Do NOT use toISOString().slice(0,10) — that returns UTC and rolls over
// a day whenever the browser's local wall-clock crosses midnight relative
// to UTC (i.e. evenings west of UTC, mornings east of UTC), causing
// bookings to be created on the wrong calendar date.
function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}


  useEffect(() => {
    // Load available time slots for the selected day.
    // Individual: GET /public/availability with picked services + chosen staff.
    // Group: POST /public/availability/group with each member's services
    //        and staff pick — backend finds slots where ALL members can be
    //        served simultaneously by DISTINCT, available techs.
    if (!date) { setSlots([]); return; }
    const dStr = fmtLocalDate(date);
    setLoadingSlots(true);
    if (mode === "individual") {
      if (picked.length === 0) { setSlots([]); setLoadingSlots(false); return; }
      const serviceIdsParam = picked.join(",");
      const staffIdParam = staffId === "any" ? "" : staffId;
      api.get(`/public/availability`, { params: { date: dStr, service_ids: serviceIdsParam, staff_id: staffIdParam } })
        .then(({ data }) => { setSlots(data.slots || []); setSlotsReason(""); })
        .catch(() => { setSlots([]); setSlotsReason(""); })
        .finally(() => setLoadingSlots(false));
    } else if (mode === "combo") {
      // Single-customer combo: 2-3 sequential segments back-to-back.
      // `members` array is reused as segments (each member is one segment).
      if (members.length < 2 || members.some((m) => m.services.length === 0)) {
        setSlots([]); setLoadingSlots(false); return;
      }
      api.post(`/public/availability/combo`, {
        date: dStr,
        segments: members.map((m) => ({
          service_ids: m.services,
          staff_id: m.staffId === "any" ? null : m.staffId,
        })),
      })
        .then(({ data }) => {
          // Backend returns full chains; we surface only start_times in `slots`
          // and remember the full chain so the submit step can pass it through.
          const chains = data.slots || [];
          setSlots(chains.map((c) => c.start_time));
          setSlotsReason(data.reason || "");
          setComboChain(chains);
        })
        .catch(() => { setSlots([]); setSlotsReason(""); })
        .finally(() => setLoadingSlots(false));
    } else {
      // group: only fetch when every member has chosen at least one service
      if (members.length < 2 || members.some((m) => m.services.length === 0)) {
        setSlots([]); setLoadingSlots(false); return;
      }
      api.post(`/public/availability/group`, {
        date: dStr,
        members: members.map((m) => ({
          service_ids: m.services,
          staff_id: m.staffId === "any" ? null : m.staffId,
        })),
      })
        .then(({ data }) => { setSlots(data.slots || []); setSlotsReason(data.reason || ""); })
        .catch(() => { setSlots([]); setSlotsReason(""); })
        .finally(() => setLoadingSlots(false));
    }
  }, [date, picked, staffId, mode, members]);

  const eligibleStaff = staff.filter((s) =>
    picked.length === 0 ? true : picked.every((sid) => s.skills.includes(sid)));
  const totalDuration = picked.reduce((sum, sid) =>
    sum + (services.find((s) => s.id === sid)?.duration_minutes || 0), 0);
  // Preserve the order that backend's /public/services returned. Backend
  // already sorts by (category_order, sort_order, name) — see booking_router.py
  // public_services(). We must NOT call .sort() here or we'd clobber that order
  // and the admin's drag-to-reorder would never show up for customers.
  const servicesByCategory = services.reduce((acc, s) => {
    const cat = s.parent_category || s.category || "Other"; (acc[cat] ||= []).push(s); return acc;
  }, {});
  const categories = [];
  services.forEach((s) => {
    const cat = s.parent_category || s.category || "Other";
    if (!categories.includes(cat)) categories.push(cat);
  });
  // RADIO LOGIC: only ONE service per parent_category (prevents 700-min spam-bookings).
  // Toggling a service replaces any other service from the same category.
  const togglePicked = (sid) => {
    const svc = services.find((x) => x.id === sid);
    if (!svc) return;
    const cat = svc.parent_category || svc.category || "Other";
    const sameCatIds = (servicesByCategory[cat] || []).map((s) => s.id);
    setPicked((p) => {
      // If clicking already-selected → deselect
      if (p.includes(sid)) return p.filter((x) => x !== sid);
      // Else: drop any other from same category, add this one
      const cleaned = p.filter((x) => !sameCatIds.includes(x));
      return [...cleaned, sid];
    });
  };
  // Sub-step on staff page for "partial-skill confirmation": when the user
  // picked a stylist who can't do every selected service, hitting Next shows
  // a full-page warning instead of jumping to step 4. Owner request: cleaner
  // 1-button choice (no inline "Use any" distraction).
  const [partialConfirm, setPartialConfirm] = useState(false);
  // NEW flow (Jun 9 redesign): after picking the mode, customer first chooses
  //   "Anyone" → green floating card, any tech, jumps straight to services
  //   "Specific Technician" → shows the tech roster (photo + name), tech gets locked
  // Drives whether step 3 (staff selection) is shown or skipped.
  const [preference, setPreference] = useState("any");
  // Custom navigation that skips the staff step when preference is "any".
  const next = () => setStep((s) => {
    // 3 (services) → 4 (staff) → 5 (date). When user picked "any",
    // we skip step 4 and go straight to date.
    if (s === 3 && preference === "any") return 5;
    return s + 1;
  });
  const back = () => {
    setPartialConfirm(false);
    setStep((s) => {
      // Reverse skip: date (5) → services (3) when "any".
      if (s === 5 && preference === "any") return 3;
      return Math.max(0, s - 1);
    });
  };

  const submitIndividual = async () => {
    if (!name.trim()) return toast.error(t("name_required"));
    if (phone.replace(/\D/g, "").length < 10) return toast.error(t("invalid_phone"));
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/booking", {
        customer_phone: phone.replace(/\D/g, ""),
        customer_name: name.trim(), customer_notes: notes,
        service_ids: picked, staff_id: staffId === "any" ? null : staffId,
        date: fmtLocalDate(date), start_time: time,
      });
      nav(`/booking/${data.id}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || t("booking_failed")); }
    finally { setSubmitting(false); }
  };

  const submitGroup = async () => {
    if (!name.trim()) return toast.error(t("name_required"));
    if (phone.replace(/\D/g, "").length < 10) return toast.error(t("invalid_phone"));
    if (members.some((m) => !m.name.trim() || m.services.length === 0))
      return toast.error("Each person needs name + at least one service");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/group-booking", {
        contact_phone: phone.replace(/\D/g, ""), contact_name: name.trim(),
        customer_notes: notes,
        members: members.map((m) => ({
          customer_name: m.name.trim(), service_ids: m.services,
          staff_id: m.staffId === "any" ? null : m.staffId,
        })),
        date: fmtLocalDate(date), start_time: time,
      });
      nav(`/booking/${data.bookings[0].id}?group=${data.group_id}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || t("booking_failed")); }
    finally { setSubmitting(false); }
  };

  const submitCombo = async () => {
    if (!name.trim()) return toast.error(t("name_required"));
    if (phone.replace(/\D/g, "").length < 10) return toast.error(t("invalid_phone"));
    if (members.some((m) => m.services.length === 0))
      return toast.error("Each segment needs at least one service");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/combo-booking", {
        contact_name: name.trim(),
        contact_phone: phone.replace(/\D/g, ""),
        customer_notes: notes,
        date: fmtLocalDate(date),
        start_time: time,
        segments: members.map((m) => ({
          service_ids: m.services,
          staff_id: m.staffId === "any" ? null : m.staffId,
        })),
      });
      nav(`/booking/${data.bookings[0].id}?group=${data.group_id}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || t("booking_failed")); }
    finally { setSubmitting(false); }
  };

  const totalSteps = 6;
  // Step order (Jun 16 — services before staff per owner request):
  //   0: welcome (mode picker)
  //   1: info  (name + phone, auto-fill known customer)
  //   2: preference (Anyone vs Specific Technician)
  //   3: services
  //   4: staff selection (SKIPPED when preference="any")
  //   5: date + time (confirm button)
  const stepValid = () => {
    if (step === 1) return !!name.trim() && phone.replace(/\D/g, "").length === 10;
    if (step === 2) return preference === "any" || preference === "specific";
    if (mode === "individual") {
      if (step === 3) return picked.length > 0;
      if (step === 4) return preference === "any" || !!staffId;
      if (step === 5) return !!date && !!time;
    } else if (mode === "combo") {
      if (step === 3) return members.length >= 2 && members.length <= 3 && members.every((m) => m.services.length > 0);
      if (step === 4) return preference === "any" || members.every((m) => m.staffId);
      if (step === 5) return !!date && !!time;
    } else {
      if (step === 3) return members.length >= 2 && members.every((m) => m.name.trim() && m.services.length > 0);
      if (step === 4) return preference === "any" || members.every((m) => m.staffId);
      if (step === 5) return !!date && !!time;
    }
    return true;
  };

  // Suggestion helper: for a list of service ids, return the first service
  // (and its lastStylist) that this customer previously booked. Returns null
  // if no history applies.
  const findSuggestion = (svcIds, eligibleSet) => {
    for (const sid of svcIds) {
      const last = lastByService[sid];
      if (last && eligibleSet.has(last)) {
        const svc = services.find((s) => s.id === sid);
        const stylist = staff.find((s) => s.id === last);
        if (svc && stylist) return { service: svc, stylist };
      }
    }
    return null;
  };

  const renderServicesList = (picked, toggleFn) => (
    <div className="space-y-8">
      {categories.map((cat) => (
        <div key={cat}>
          <div className="text-xl font-bold text-zen-ink mb-3 font-serif">{cat}</div>
          <div className="space-y-2">
            {servicesByCategory[cat].map((s) => {
              const checked = picked.includes(s.id);
              const priceLabel = s.price > 0
                ? (s.price_is_minimum ? `$${s.price}+` : `$${s.price}`)
                : "";
              return (
                <button key={s.id} data-testid={`service-${s.id}`} onClick={() => toggleFn(s.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${checked ? "border-zen-primary bg-zen-primary text-white shadow-md" : "border-zen-border bg-white hover:border-zen-primary"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm ${checked ? "text-white" : "text-zen-ink"}`}>{lang === "es" && s.name_es ? s.name_es : s.name}</div>
                      {s.price_is_minimum && (
                        <div className={`text-[10px] mt-1 uppercase tracking-wider ${checked ? "text-white/70" : "text-zen-muted"}`}>
                          {t("starting_at") || "Starting at"}
                        </div>
                      )}
                    </div>
                    {priceLabel && (
                      <div
                        className={`font-bold text-xl whitespace-nowrap ${checked ? "text-white" : "text-zen-cal-yellow"}`}
                        data-testid={`service-price-${s.id}`}
                      >
                        {priceLabel}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    // PARTIAL-SKILL CONFIRMATION OVERLAY — shown when user tried to advance
    // past the services step but their chosen Individual stylist can't do
    // every picked service. Single big CTA per owner: switch to combo with
    // pre-filled segments. Lives outside the step blocks so it can render on
    // top of whatever step the user is on.
    if (partialConfirm && mode === "individual") {
      const pickedStaff = staff.find((s) => s.id === staffId);
      if (pickedStaff && picked.length > 0 && !picked.every((sid) => pickedStaff.skills.includes(sid))) {
        const yes = picked
          .filter((sid) => pickedStaff.skills.includes(sid))
          .map((sid) => services.find((x) => x.id === sid)?.name)
          .filter(Boolean)
          .join(", ");
        const no = picked
          .filter((sid) => !pickedStaff.skills.includes(sid))
          .map((sid) => services.find((x) => x.id === sid)?.name)
          .filter(Boolean)
          .join(", ");
        return (
          <div data-testid="partial-skill-confirm">
            <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-6">Heads up</h2>
            <div className="p-6 rounded-2xl bg-amber-50 border-2 border-amber-300 space-y-5">
              <p className="text-base text-amber-900 leading-relaxed">
                <span className="font-bold">{pickedStaff.name}</span> only does <span className="font-bold">{yes || "—"}</span>.
                <br /><br />
                They don&apos;t do <span className="font-bold">{no}</span>. Would you like to pick another stylist for that part?
              </p>
              <button onClick={() => {
                // Build combo segments, jump back to staff step (3) so user
                // can confirm/change Segment 2's stylist, then continue.
                const yesIds = picked.filter((sid) => pickedStaff.skills.includes(sid));
                const noIds  = picked.filter((sid) => !pickedStaff.skills.includes(sid));
                setMembers([
                  { name: "", services: yesIds, staffId: pickedStaff.id },
                  { name: "", services: noIds, staffId: "any" },
                ]);
                setMode("combo");
                setPreference("specific");
                setPartialConfirm(false);
                setStep(3);
              }}
                className="w-full rounded-full bg-amber-600 text-white text-base font-semibold px-6 py-3 hover:bg-amber-700 shadow-md flex items-center justify-center gap-2"
                data-testid="partial-pick-another-btn">
                Pick another stylist for {no} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <button onClick={() => setPartialConfirm(false)}
              className="mt-6 text-sm text-zen-muted hover:text-zen-ink underline"
              data-testid="partial-back-btn">
              ← Back
            </button>
          </div>
        );
      }
    }

    if (step === 0) return (
      <div data-testid="step-welcome">
        <div className="text-xs tracking-[0.25em] uppercase text-zen-muted mb-4">— Florida</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-zen-ink leading-[1.05] mb-4">{t("salon_name")}</h1>
        <p className="text-zen-muted text-lg mb-12 max-w-md leading-relaxed">{t("a_moment_of_calm")}</p>
        <div className="grid sm:grid-cols-3 gap-4 mb-10">
          <button data-testid="mode-individual" onClick={() => { setMode("individual"); next(); }}
            className="p-6 rounded-2xl border border-zen-border hover:border-zen-primary text-left bg-white">
            <div className="text-zen-ink font-medium text-lg mb-1">{t("individual")}</div>
            <div className="text-sm text-zen-muted">One person, one stylist</div>
          </button>
          <button data-testid="mode-combo" onClick={() => {
            // Combo: 1 customer, multiple sequential stylists (Tom does pedi
            // 9:30→10:30, then Hai does mani 10:30→12:00). Two segments by default.
            setMode("combo");
            setMembers([{ name: "", services: [], staffId: "any" }, { name: "", services: [], staffId: "any" }]);
            next();
          }} className="p-6 rounded-2xl border border-zen-border hover:border-zen-primary text-left bg-white">
            <div className="text-zen-ink font-medium text-lg mb-1">Combo</div>
            <div className="text-sm text-zen-muted">One person, multiple stylists (back-to-back)</div>
          </button>
          <button data-testid="mode-group" onClick={() => {
            setMode("group");
            setMembers([{ name: "", services: [], staffId: "any" }, { name: "", services: [], staffId: "any" }]);
            next();
          }} className="p-6 rounded-2xl border border-zen-border hover:border-zen-primary text-left bg-white">
            <div className="text-zen-ink font-medium text-lg mb-1">{t("group")} (2–4)</div>
            <div className="text-sm text-zen-muted">Multiple people, same time</div>
          </button>
        </div>
      </div>
    );

    // STEP 1 — Customer info (name + phone)
    if (step === 1) return (
      <div data-testid="step-info">
        <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-2">{t("your_information")}</h2>
        <p className="text-sm text-zen-muted mb-8">{t("info_helper")}</p>
        <div className="space-y-4 mb-2">
          <Input placeholder={t("phone_number")} value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            className="bg-zen-surface border-0 rounded-lg h-12" data-testid="customer-phone-input" />
          <Input placeholder={t("full_name")} value={name} onChange={(e) => setName(e.target.value)}
            className="bg-zen-surface border-0 rounded-lg h-12" data-testid="customer-name-input" />
          <Textarea placeholder={t("notes_placeholder")} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="bg-zen-surface border-0 rounded-lg min-h-24" data-testid="customer-notes-input" />
        </div>
        {customerKnown && name && (
          <div className="mt-3 text-sm text-zen-primary" data-testid="welcome-back">
            👋 {t("welcome_back")}, {name.split(" ")[0]}!
          </div>
        )}
        {upcomingAppts.length > 0 && (
          <div className="mt-6 p-5 rounded-2xl bg-amber-50 border-2 border-amber-300" data-testid="upcoming-reminder">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm font-bold text-lg">
                !
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-serif text-lg text-amber-900 mb-1">
                  You already have {upcomingAppts.length === 1 ? "an upcoming appointment" : `${upcomingAppts.length} upcoming appointments`}
                </div>
                <div className="text-xs text-amber-800 mb-3 leading-snug">
                  To avoid double-booking, please review before continuing. Reply <span className="font-bold">CANCEL</span> to any of our texts to cancel.
                </div>
                <div className="space-y-2">
                  {upcomingAppts.map((a) => {
                    const dt = new Date(a.date + "T12:00:00");
                    const dateStr = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                    const [h, m] = (a.start_time || "00:00").split(":");
                    const hr = parseInt(h); const ampm = hr >= 12 ? "PM" : "AM";
                    const h12 = hr % 12 || 12;
                    const timeStr = `${h12}:${m} ${ampm}`;
                    return (
                      <div key={a.id} className="bg-white rounded-lg p-3 border border-amber-200" data-testid={`upcoming-appt-${a.id}`}>
                        <div className="font-semibold text-sm text-zen-ink">{dateStr} at {timeStr}</div>
                        <div className="text-xs text-zen-muted mt-0.5">{a.services_summary}</div>
                        <div className="text-xs text-zen-muted">
                          {a.requested_any ? "Any technician" : `with ${a.stylist_name}`}
                          {a.status === "confirmed" && <span className="ml-2 text-emerald-700 font-semibold">· Confirmed</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    // STEP 2 — Services
    if (step === 3 && mode === "individual") return (
      <div data-testid="step-services">
        <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-2">{t("select_services")}</h2>
        <p className="text-sm text-zen-muted mb-8">{t("multi_select_hint")}</p>
        {renderServicesList(picked, togglePicked)}
      </div>
    );

    if (step === 3 && (mode === "group" || mode === "combo")) return (
      <div data-testid="step-group-services">
        <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-2">
          {mode === "combo" ? "Combo segments" : t("group")}
        </h2>
        {mode === "combo" && (
          <p className="text-sm text-zen-muted mb-4">
            Pick services for each segment. Each segment runs back-to-back with a different stylist.
          </p>
        )}
        {members.map((m, i) => {
          // Same logic as step 3: show service names for combo segments
          // so the customer knows what they're picking (no "Segment 1/2").
          const seg2Names = m.services
            .map((sid) => services.find((x) => x.id === sid))
            .filter(Boolean)
            .map((s) => (lang === "es" && s.name_es ? s.name_es : s.name))
            .join(", ");
          const headerLabel = mode === "combo"
            ? (seg2Names ? seg2Names : `Segment ${i + 1}`)
            : `${t("person")} ${i + 1}`;
          return (
          <div key={i} className="mb-6 p-5 rounded-xl border border-zen-border bg-white" data-testid={`group-member-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.2em] text-zen-muted">
                {headerLabel}
              </div>
              {members.length > 2 && (
                <button onClick={() => setMembers(members.filter((_, j) => j !== i))} className="text-xs text-zen-muted hover:text-red-600 flex items-center gap-1">
                  <Trash2 className="w-3 h-3" /> {t("remove")}
                </button>
              )}
            </div>
            {/* Per-person name is GROUP-only; combo shares the contact's name. */}
            {mode === "group" && (
              <Input placeholder={t("full_name")} value={m.name}
                onChange={(e) => { const c = [...members]; c[i].name = e.target.value; setMembers(c); }}
                className="bg-zen-surface border-0 rounded-lg mb-3" data-testid={`group-member-${i}-name`} />
            )}
            {categories.map((cat) => (
              <div key={cat} className="mb-3">
                <div className="text-base font-bold font-serif text-zen-ink mb-2">{cat}</div>
                <div className="space-y-1">
                  {servicesByCategory[cat].map((s) => {
                    const checked = m.services.includes(s.id);
                    const sameCatIds = servicesByCategory[cat].map((x) => x.id);
                    return (
                      <button key={s.id} onClick={() => {
                        const c = [...members];
                        if (checked) {
                          c[i].services = m.services.filter((x) => x !== s.id);
                        } else {
                          const cleaned = m.services.filter((x) => !sameCatIds.includes(x));
                          c[i].services = [...cleaned, s.id];
                          c[i].staffId = "any";
                        }
                        setMembers(c);
                      }} className={`w-full p-2.5 rounded-lg border text-left text-sm flex items-center justify-between gap-3 ${checked ? "border-zen-primary bg-zen-surface" : "border-zen-border bg-white"}`}>
                        <span className="flex-1 min-w-0">{lang === "es" && s.name_es ? s.name_es : s.name}</span>
                        {s.price > 0 && (
                          <span className="text-sm font-bold text-zen-cal-yellow whitespace-nowrap">
                            {s.price_is_minimum ? `$${s.price}+` : `$${s.price}`}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );})}
        {((mode === "group" && members.length < 4) || (mode === "combo" && members.length < 3)) && (
          <button onClick={() => setMembers([...members, { name: "", services: [], staffId: "any" }])}
            className="w-full p-4 border border-dashed border-zen-border rounded-xl text-zen-muted hover:text-zen-ink hover:border-zen-primary flex items-center justify-center gap-2"
            data-testid="add-person-btn">
            <Plus className="w-4 h-4" /> {mode === "combo" ? "Add segment" : t("add_person")}
          </button>
        )}
      </div>
    );

    // STEP 2 — Technician preference (Anyone vs Specific) — gates whether
    // step 3 (staff selection) is shown. "Anyone" routes the customer
    // straight to services. Green floating-card per owner request: any
    // booking with preference=any (== requested_any=true) renders green on
    // the turn board and can float to any available tech.
    if (step === 2) return (
      <div data-testid="step-preference">
        <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-2">Technician preference</h2>
        <p className="text-sm text-zen-muted mb-6">
          {mode === "group"
            ? "Pick one option for the whole group. Individual stylists are chosen on the next step."
            : mode === "combo"
              ? "For multi-service combo bookings."
              : "How would you like to book?"}
        </p>
        <div className="space-y-3">
          <button onClick={() => {
            setPreference("any");
            setStaffId("any");
            setMembers((arr) => arr.map((m) => ({ ...m, staffId: "any" })));
          }}
            className={`w-full p-5 rounded-xl border-2 text-left transition ${preference === "any" ? "border-emerald-500 bg-emerald-50 shadow-md" : "border-zen-border bg-white hover:border-emerald-400"}`}
            data-testid="pref-any">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0 shadow-sm">
                <Users className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-zen-ink text-base">Anyone</div>
                <div className="text-sm text-zen-muted mt-0.5 leading-snug">
                  Any available technician. Faster, often shorter wait.
                </div>
              </div>
              {preference === "any" && (
                <div className="text-emerald-600 shrink-0 mt-1">
                  <Check className="w-5 h-5" strokeWidth={2.5} />
                </div>
              )}
            </div>
          </button>
          <button onClick={() => setPreference("specific")}
            className={`w-full p-5 rounded-xl border-2 text-left transition ${preference === "specific" ? "border-zen-primary bg-zen-surface shadow-md" : "border-zen-border bg-white hover:border-zen-primary"}`}
            data-testid="pref-specific">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-zen-primary text-white flex items-center justify-center shrink-0 shadow-sm">
                <UserIcon className="w-6 h-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-zen-ink text-base">
                  {mode === "combo" ? "Specific Technicians" : mode === "group" ? "Each person picks their own" : "Specific Technician"}
                </div>
                <div className="text-sm text-zen-muted mt-0.5 leading-snug">
                  {mode === "combo"
                    ? "Choose a stylist for each service in your combo."
                    : mode === "group"
                      ? "Every person in the group selects their preferred stylist."
                      : "Choose your preferred stylist."}
                </div>
              </div>
              {preference === "specific" && (
                <div className="text-zen-primary shrink-0 mt-1">
                  <Check className="w-5 h-5" strokeWidth={2.5} />
                </div>
              )}
            </div>
          </button>
        </div>
      </div>
    );

    // STEP 3 — Staff selection (with smart suggestion for returning customers)
    if (step === 4 && mode === "individual") {
      // Owner request: show ALL active staff — including ones who can't do
      // every picked service. For staff with partial skills (e.g. Hai only
      // does fullset), tag them as such (no color tint — same look as
      // eligible staff). Warning + recovery CTA shows only after they click
      // Next, not inline below the list.
      const eligibleSet = new Set(eligibleStaff.map((s) => s.id));
      const partial = (s) => picked.length > 0 && !eligibleSet.has(s.id);
      const skillNames = (s) => picked
        .filter((sid) => s.skills.includes(sid))
        .map((sid) => services.find((x) => x.id === sid)?.name)
        .filter(Boolean)
        .join(", ");
      const allStaff = staff;
      const suggestion = findSuggestion(picked, eligibleSet);
      const suggestedId = suggestion?.stylist?.id;
      const sortedAll = [...allStaff].sort((a, b) => {
        if (a.id === suggestedId) return -1;
        if (b.id === suggestedId) return 1;
        const ap = partial(a) ? 1 : 0;
        const bp = partial(b) ? 1 : 0;
        return ap - bp; // eligible first, partial last
      });
      return (
        <div data-testid="step-staff">
          <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-4">{t("choose_staff")}</h2>
          {suggestion && (
            <div className="mb-6 p-4 rounded-xl bg-zen-surface border border-zen-primary/30 text-sm" data-testid="stylist-suggestion">
              💡 {t("last_time_with_prefix")} <span className="font-medium">{suggestion.service.name}</span> {t("with_word")} <span className="font-medium">{suggestion.stylist.name}</span>. {t("book_again_q")}
            </div>
          )}
          <div className="space-y-3">
            {/* When preference="any" we wouldn't even be on this step, so
                drop the "Any one" pseudo-button. Customer is here BECAUSE
                they want a specific stylist. */}
            {sortedAll.map((s) => {
              const isPartial = partial(s);
              const selected = staffId === s.id;
              const initial = (s.name || "?").trim().charAt(0).toUpperCase();
              return (
                <button key={s.id} onClick={() => setStaffId(s.id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition ${selected ? "border-zen-primary bg-zen-primary text-white shadow-md" : "border-zen-border bg-white hover:border-zen-primary"}`}
                  data-testid={`staff-${s.id}`}>
                  <div className="flex items-center gap-4">
                    {/* Initial-circle "photo" — clean placeholder until real
                        photo upload is wired in Manage Staff. */}
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center font-serif text-xl shrink-0 shadow-sm ${selected ? "bg-white text-zen-primary" : "bg-zen-primary text-white"}`}>
                      {initial}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`font-medium text-base ${selected ? "text-white" : "text-zen-ink"}`}>{s.name}</div>
                      {isPartial && (
                        <div className={`text-sm mt-1 font-medium ${selected ? "text-white/90" : "text-amber-700"}`}>
                          Only does {skillNames(s) || "—"}
                        </div>
                      )}
                    </div>
                    {s.id === suggestedId && (
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${selected ? "bg-white/20 text-white" : "bg-zen-primary/10 text-zen-primary"}`}>
                        {t("suggested")}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    if (step === 4 && (mode === "group" || mode === "combo")) return (
      <div>
        <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-8">{t("choose_staff")}</h2>
        {members.map((m, i) => {
          const eligible = staff.filter((s) => m.services.every((sid) => s.skills.includes(sid)));
          const eligibleSet = new Set(eligible.map((s) => s.id));
          const suggestion = findSuggestion(m.services, eligibleSet);
          // Use the picked service names (joined) as the segment label so
          // the customer sees what stylist they're choosing FOR (e.g.,
          // "Full Set", "Pedicure") instead of opaque "Segment 1/2".
          const segServiceNames = m.services
            .map((sid) => services.find((x) => x.id === sid))
            .filter(Boolean)
            .map((s) => (lang === "es" && s.name_es ? s.name_es : s.name))
            .join(", ");
          const memberLabel = mode === "combo"
            ? (segServiceNames || `Segment ${i + 1}`)
            : `${t("person")} ${i + 1}: ${m.name || `#${i + 1}`}`;
          return (
            <div key={i} className="mb-4 p-4 rounded-xl border border-zen-border bg-white" data-testid={`group-member-${i}-staff-row`}>
              <div className="text-xs uppercase tracking-[0.2em] text-zen-muted mb-2">
                {memberLabel}
              </div>
              {suggestion && (
                <div className="mb-2 text-xs text-zen-primary">
                  💡 {t("last_time_with_prefix")} {suggestion.service.name} {t("with_word")} {suggestion.stylist.name}
                </div>
              )}
              {/* Card grid picker with photo circles + names. Customer
                  reached this step because preference="specific", so we drop
                  the "Any one" option from the list — clearer commitment. */}
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const techsToShow = suggestion
                    ? [suggestion.stylist, ...eligible.filter((s) => s.id !== suggestion.stylist.id)]
                    : eligible;
                  return techsToShow.map((s) => {
                    const init = (s.name || "?").trim().charAt(0).toUpperCase();
                    const sel = m.staffId === s.id;
                    return (
                      <button key={s.id} onClick={() => { const c = [...members]; c[i].staffId = s.id; setMembers(c); }}
                        className={`p-3 rounded-xl border-2 flex items-center gap-2 text-left transition ${sel ? "border-zen-primary bg-zen-primary text-white shadow-md" : "border-zen-border bg-white hover:border-zen-primary"}`}
                        data-testid={`group-member-${i}-staff-${s.id}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-serif text-base shrink-0 ${sel ? "bg-white text-zen-primary" : "bg-zen-primary text-white"}`}>
                          {init}
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm font-medium truncate ${sel ? "text-white" : "text-zen-ink"}`}>{s.name}</div>
                          {suggestion && s.id === suggestion.stylist.id && (
                            <div className={`text-[10px] mt-0.5 ${sel ? "text-white/80" : "text-zen-primary"}`}>{t("suggested")}</div>
                          )}
                        </div>
                      </button>
                    );
                  });
                })()}
              </div>
            </div>
          );
        })}
      </div>
    );

    // STEP 4 — Date + Time + Summary
    if (step === 5) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const max = new Date(); max.setMonth(max.getMonth() + 6);
      return (
        <div data-testid="step-datetime">
          <h2 className="font-serif text-3xl lg:text-4xl text-zen-ink mb-8">{t("select_date")}</h2>
          <div className="rounded-3xl border border-zen-border bg-white p-4 sm:p-6 mb-6 inline-block shadow-md">
            <Calendar mode="single" selected={date} onSelect={(d) => { setDate(d); setTime(""); }}
              disabled={(d) => d < today || d > max}
              weekStartsOn={1}
              formatters={{
                formatWeekdayName: (d) => ["S", "M", "T", "W", "T", "F", "S"][d.getDay()],
              }}
              className="rounded-md"
              classNames={{
                months: "flex flex-col space-y-4",
                caption: "flex justify-center pt-1 relative items-center mb-3",
                caption_label: "text-lg sm:text-xl font-serif text-zen-ink tracking-wide",
                nav_button: "h-9 w-9 bg-white border border-zen-border rounded-full hover:border-zen-primary hover:bg-zen-primary hover:text-white inline-flex items-center justify-center transition",
                table: "w-full border-collapse",
                head_row: "flex mb-2",
                head_cell: "text-zen-muted rounded-md w-10 sm:w-11 font-semibold text-[11px] uppercase tracking-[0.18em]",
                row: "flex w-full mt-1.5",
                cell: "h-10 w-10 sm:h-11 sm:w-11 text-center text-sm p-0 relative",
                day: "h-10 w-10 sm:h-11 sm:w-11 p-0 font-medium rounded-full hover:bg-zen-surface text-sm transition",
                day_selected: "bg-zen-primary text-white hover:bg-zen-primary-hover hover:text-white shadow-md ring-2 ring-zen-primary/30",
                day_today: "bg-zen-cal-yellow/15 text-zen-ink font-bold ring-1 ring-zen-cal-yellow/40",
                day_outside: "text-zen-muted/30",
                day_disabled: "text-zen-muted/25 cursor-not-allowed hover:bg-transparent",
              }}
            />
          </div>
          {date && (
            <>
              <h3 className="font-serif text-2xl text-zen-ink mb-4">{t("select_time")}</h3>
              {loadingSlots ? <div className="text-zen-muted">Loading…</div>
                : slots.length === 0 ? (
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-200" data-testid="no-slots-reason">
                    <div className="text-sm font-semibold text-amber-900 mb-1">No availability</div>
                    <div className="text-sm text-amber-800">{slotsReason || t("no_slots")}</div>
                  </div>
                )
                : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="time-slots">
                    {slots.map((s) => (
                      <button key={s} onClick={() => setTime(s)} data-testid={`slot-${s}`}
                        className={`py-3.5 px-4 rounded-full border-2 text-base font-medium ${time === s ? "bg-zen-primary text-white border-zen-primary shadow-md" : "border-zen-border bg-white hover:border-zen-primary"}`}>
                        {formatTime12(s)}
                      </button>
                    ))}
                  </div>
                )}
            </>
          )}
          {/* Summary */}
          <div className="border-t border-zen-border pt-6 mt-8 space-y-2 text-sm">
            <div className="text-xs uppercase tracking-[0.2em] text-zen-muted mb-2">{t("summary")}</div>
            {mode === "individual" ? (
              <>
                <div><span className="text-zen-muted">{t("services")}:</span> {picked.map((id) => services.find((s) => s.id === id)?.name).join(", ")}</div>
                <div><span className="text-zen-muted">{t("stylist")}:</span> {staffId === "any" ? t("any_one") : staff.find((s) => s.id === staffId)?.name}</div>
              </>
            ) : mode === "combo" ? (
              <div><span className="text-zen-muted">Combo:</span> {members.length} segments, back-to-back</div>
            ) : <div><span className="text-zen-muted">Group:</span> {members.length} people</div>}
            <div><span className="text-zen-muted">{t("date")}:</span> {date?.toLocaleDateString("en-US")}</div>
            <div><span className="text-zen-muted">{t("time")}:</span> {time ? formatTime12(time) : "—"}</div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen lg:flex bg-zen-bg">
      <aside className="hidden lg:block lg:w-2/5 xl:w-[45%] sticky top-0 h-screen overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-zen-ink/30 to-zen-ink/60 z-10" />
        <img src={SIDEBAR_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="relative z-20 h-full flex flex-col justify-end p-12 text-white">
          <div className="text-xs tracking-[0.3em] uppercase mb-4 opacity-80">— A House of Care</div>
          <div className="font-serif text-5xl xl:text-6xl leading-[1.05] mb-3">Zen Nails Lounge & Spa</div>
          <div className="text-sm opacity-90 max-w-md">Quiet hands. Considered detail. Florida.</div>
        </div>
      </aside>

      <main className="flex-1 min-h-screen flex flex-col">
        <header className="px-6 lg:px-16 pt-6 pb-4 flex items-center justify-between">
          <div className="lg:hidden font-serif text-2xl text-zen-ink">Zen Nail</div>
          <div className="ml-auto flex items-center gap-2">
            <Globe className="w-4 h-4 text-zen-muted" strokeWidth={1.5} />
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="border-0 bg-transparent w-[110px] h-8 text-sm" data-testid="lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </header>
        <div className="flex-1 px-6 lg:px-16 py-6 max-w-2xl w-full mx-auto">
          {step > 0 && <StepHeader step={step} total={totalSteps - 1} />}
          {renderStep()}
        </div>
        <div className="px-6 lg:px-16 py-6 border-t border-zen-border bg-white/60 backdrop-blur flex items-center justify-between gap-3 sticky bottom-0">
          <Button variant="ghost" onClick={back} disabled={step === 0}
            className="rounded-full text-zen-ink" data-testid="back-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> {t("back")}
          </Button>
          {step < totalSteps - 1 ? (
            <Button onClick={() => {
              // After picking services, intercept if the chosen Individual
              // stylist can't do every selected service. Show full-page
              // "Heads up" confirmation (offers auto-combo recovery).
              if (step === 4 && mode === "individual" && staffId && staffId !== "any" && !partialConfirm) {
                const pickedStaff = staff.find((s) => s.id === staffId);
                if (pickedStaff && picked.length > 0 && !picked.every((sid) => pickedStaff.skills.includes(sid))) {
                  setPartialConfirm(true);
                  return;
                }
              }
              next();
            }} disabled={!stepValid()}
              className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white px-8"
              data-testid="next-btn">
              {t("next")} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={mode === "individual" ? submitIndividual : mode === "combo" ? submitCombo : submitGroup}
              disabled={submitting || !date || !time}
              className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white px-8"
              data-testid="confirm-booking-btn">
              <Sparkles className="w-4 h-4 mr-2" /> {t("confirm_booking")}
            </Button>
          )}
        </div>
      </main>
      {/* AI Chat bubble hidden per owner request (May 28). Code kept for
          later — uncomment to re-enable. */}
      {/* <ChatBubble lang={lang} /> */}
    </div>
  );
}
